import React from "react";
import { useAuth } from "../context/auth-context";
import { usePermissionHint } from "../context/permission-hint-context";
import { showSwal } from "@/components/ui/app-swal";

interface RequirePermissionProps {
  children: React.ReactNode;
  /** Uno o varios permisos (OR): basta con que el usuario tenga uno. */
  permission: string | string[];
  fallback?: React.ReactNode;
}

/**
 * Componente que protege rutas o secciones basándose en permisos del usuario
 * Si el usuario no tiene el permiso requerido, redirige a una página de acceso denegado
 * o muestra un componente de fallback.
 */
export const RequirePermission: React.FC<RequirePermissionProps> = ({
  children,
  permission,
  fallback,
}) => {
  const { hasPermission, permissionsLoading, permissions } = useAuth();
  const { reportDenied } = usePermissionHint();
  const normalizePermissionCode = React.useCallback(
    (value: string) => value.trim().replace(/[.\s]+/g, ".").toUpperCase(),
    [],
  );

  const requiredPermissions = React.useMemo(
    () => (Array.isArray(permission) ? permission : [permission]),
    [permission],
  );
  const normalizedUserPermissions = React.useMemo(() => {
    return new Set(permissions.map((item) => normalizePermissionCode(item)));
  }, [permissions, normalizePermissionCode]);
  const hasRequiredPermission = hasPermission(permission);
  const deniedKeyRef = React.useRef<string | null>(null);
  const deniedModalShownRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (permissionsLoading) return;
    console.log("[RequirePermission] Validando permiso:", {
      permission,
      userPermissions: permissions,
      hasPermission: hasRequiredPermission,
      permissionsLoading,
    });
  }, [permission, permissions, hasRequiredPermission, permissionsLoading]);

  React.useEffect(() => {
    if (permissionsLoading || hasRequiredPermission) {
      deniedKeyRef.current = null;
      deniedModalShownRef.current = null;
      return;
    }

    const missingPermissions = requiredPermissions.filter(
      (item) => !normalizedUserPermissions.has(normalizePermissionCode(item)),
    );
    const deniedKey = `${requiredPermissions.join("|")}::${missingPermissions.join("|")}`;
    if (deniedKeyRef.current === deniedKey) return;
    deniedKeyRef.current = deniedKey;

    reportDenied({
      scope: "route",
      resource:
        typeof window !== "undefined" ? window.location.pathname : "unknown-route",
      actionLabel: "Acceder a ruta protegida",
      requiredPermissions,
      missingPermissions,
    });
  }, [
    hasRequiredPermission,
    permissions,
    permissionsLoading,
    reportDenied,
    requiredPermissions,
    normalizePermissionCode,
    normalizedUserPermissions,
  ]);

  React.useEffect(() => {
    if (permissionsLoading || hasRequiredPermission) return;
    const deniedKey = deniedKeyRef.current ?? requiredPermissions.join("|");
    if (deniedModalShownRef.current === deniedKey) return;
    deniedModalShownRef.current = deniedKey;

    void showSwal({
      icon: "warning",
      title: "Permisos insuficientes",
      text: "No tienes los permisos necesarios para realizar esta acción.",
      confirmButtonText: "Entendido",
    });
  }, [hasRequiredPermission, permissionsLoading, requiredPermissions]);

  // Mostrar loading mientras se cargan los permisos
  if (permissionsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="text-sm text-muted-foreground">
            Cargando permisos...
          </p>
        </div>
      </div>
    );
  }

  if (!hasRequiredPermission) {
    console.warn("[RequirePermission] Usuario no tiene permiso:", {
      required: requiredPermissions,
      userPermissions: permissions,
    });

    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md text-center space-y-2">
          <p className="text-lg font-semibold">Acceso denegado</p>
          <p className="text-sm text-muted-foreground">
            No tienes permisos para acceder a esta sección.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
